<script setup lang="ts">
// Vendor
import type { Entry, EntrySkeletonType } from "contentful";
import type { ConcreteComponent } from "vue";

// Types
import {
  isTypeMembershipCompare,
  type TypeFeatureSkeleton,
  type TypeMembershipCompare,
} from "@citizenm/contentful/shared/types/generated";
import type { IMembershipFeature } from "@citizenm/membership/shared/types/comparesubscriptions";

const { content, container = false } = defineProps<{
  container?: boolean;
  content: Entry["fields"];
}>();

const { navigationState } = useNavigation();
const { locale } = useI18n();

const navigationDropdownVisible = computed(() => {
  return (
    navigationState.value.mobileDrawerOpen ||
    navigationState.value.openedNavigationDropDownId
  );
});

const components = computed<
  | {
      id: string;
      type: string;
      component: ConcreteComponent;
      data: Entry<
        EntrySkeletonType,
        "WITHOUT_UNRESOLVABLE_LINKS",
        typeof locale.value
      >;
    }[]
  | null
>(() => {
  // @ts-expect-error `fields` is typed as a generic `Entry["fields"]`, which does not include `content` specifically.
  if (!content?.fields?.content) {
    return null;
  }

  // @ts-expect-error `fields` is typed as a generic `Entry["fields"]`, which does not include `content` specifically.
  return content.fields.content.map((c, i) => ({
    id: c.sys?.id || `section-${i}`,
    type: c.sys?.contentType?.sys.id,
    component: getComponent(c.sys?.contentType?.sys.id),
    data: c,
  }));
});

const hasBookingWidget = computed(
  () => components.value?.some((c) => c.type === "bookingWidget") || false,
);

const getComponent = (type: string) => {
  switch (type) {
    case "curatedCollection":
      return resolveComponent("ContentfulCuratedCollection");
    case "richText":
      return resolveComponent("ContentfulAtomRichText");
    case "mediaAndText":
      return resolveComponent("ContentfulMediaAndText");
    case "banner":
      return resolveComponent("ContentfulBanner");
    case "image":
      return resolveComponent("ContentfulImage");
    case "video":
      return resolveComponent("ContentfulVideo");
    case "blogText":
    case "legalText":
      return resolveComponent("ContentfulBlogText");
    case "featureCollection":
      return resolveComponent("ContentfulFeaturesFeatureCollection");
    case "hero":
      return resolveComponent("ContentfulHero");
    case "mediaCollection":
      return resolveComponent("ContentfulMediaCollection");
    case "form":
      return resolveComponent("ContentfulForm");
    case "locationCollection":
      return resolveComponent("ContentfulLocationCollection");
    case "bookingWidget":
      return resolveComponent("ContentfulBookingWidget");
    case "faqCategory":
      return resolveComponent("ContentfulFaqCategory");
    default:
      return resolveComponent("ContentfulMissingComponent");
  }
};

/**
 * Membership specific logic. This logic will be housed here for the lobby app. With the new apps
 * we aim to have a more generic approach of mapping Contentful content to representational components.
 */
const resolveMembershipCompareCallToAction = ({
  ctaLabel,
  ctaUrl,
}: Partial<TMembershipCompareCallToAction>) => {
  if (!ctaLabel || !ctaUrl) {
    return null;
  }

  return {
    ctaLabel,
    ctaUrl,
  };
};

const convertMembershipFeature = (
  feature?: Entry<
    TypeFeatureSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    typeof locale.value
  >,
) => {
  if (!feature) {
    return false;
  }

  return {
    icon: feature.fields.icon?.fields.id || "checkmark",
    label: feature.fields.title,
  };
};
const extractMembershipCompareSubscriptions = (
  membershipModule: TypeMembershipCompare<
    "WITHOUT_UNRESOLVABLE_LINKS",
    typeof locale.value
  >["fields"],
): IMembershipSubscription[] => {
  return [
    membershipModule.logo1 &&
      membershipModule.description1 && {
        logo: membershipModule.logo1,
        description: membershipModule.description1,
        features:
          membershipModule.features1
            .map(convertMembershipFeature)
            .filter<IMembershipFeature>(isMembershipFeature) || [],
      },
    membershipModule.logo2 &&
      membershipModule.description2 && {
        logo: membershipModule.logo2,
        description: membershipModule.description2,
        features:
          membershipModule.features2
            ?.map(convertMembershipFeature)
            .filter<IMembershipFeature>(isMembershipFeature) || [],
      },
  ].filter<IMembershipSubscription>(isMembershipSubscription);
};
</script>

<template>
  <section
    v-if="components"
    :class="[
      'ctf-section',
      !navigationDropdownVisible &&
        hasBookingWidget &&
        components.length === 1 &&
        'ctf-section--sticky',
    ]"
  >
    <template :key="id" v-for="{ id, component, data, type } in components">
      <MembershipCompareSubscriptions
        v-if="
          data?.sys?.contentType?.sys.id &&
          isTypeMembershipCompare<'WITHOUT_UNRESOLVABLE_LINKS', typeof locale>(
            data,
          )
        "
        :call-to-action="
          resolveMembershipCompareCallToAction({
            ctaLabel: data.fields.ctaLabel,
            ctaUrl: data.fields.ctaUrl,
          })
        "
        dark
        :introduction="data.fields.introduction"
        :subscriptions="extractMembershipCompareSubscriptions(data.fields)"
        :badge="data.fields.badge"
        :title="data.fields.title"
      />
      <div
        v-else
        :class="{
          container,
        }"
      >
        <component
          :is="component"
          :content-type="type"
          :id="id"
          v-bind="data.fields"
          class="ctf-content"
          :data-component="type"
        />
      </div>
    </template>
  </section>
</template>

<style lang="scss">
section.ctf-section {
  &:not(:first-child) {
    margin-top: var(--space-56);

    @include lg {
      margin-top: var(--space-120);
    }
  }

  > *:not(:first-child) {
    margin-top: var(--space-24);

    @include lg {
      margin-top: var(--space-120);
    }
  }

  &--sticky {
    @include md {
      position: sticky;
      top: var(--header-navigation-height-mobile, 60px);
      z-index: 25;

      @include lg {
        top: var(--header-navigation-height, 80px);
      }
    }
  }
}
</style>
